import { evaluateFormulaLeftToRight } from '~/_shared/utils/number/evaluate.helpers';
import { isNullsy } from '~/_shared/utils/typeGuards';
import {
  EXPORT_REPORT_DATA_CURRENT_YEAR, EXPORT_REPORT_DATA_PROJECTED_YEAR,
} from '~/exportReport/exportReport.constants';
import {
  DEFAULT_REPORT_ITEM_FORMAT_TYPE,
  formatReportValue, getTemplateRowFormatType,
  ReportItemFormatType,
} from '~/exportReport/formatting/exportReportFormatting.helpers';
import type { ExportReportTableValue } from '~/exportReport/table/ReportTable.model';
import type { ReportTemplateDataRow } from '~/exportReport/template/reportTemplate';
import {
  getFormulaDemographicIds, isReportFormulaIsTooComplex,
} from '~/exportReport/template/reportTemplate.helpers';
import { type ReportDemographicData } from '../exportReportData.helpers';
import { normalizeReportDemographicId } from '../exportReportData.helpers';

const REPORT_VALUE_NOT_AVAILABLE = {
  value: 'N/A',
  isError: true,
} as const;

export type DemographicValue = number | string;

type GetDemographicData = (columnId: string, demographicId: string) => ReportDemographicData | null;

export const formatTemplateRowLabel = (label: string): string => label
  .replace('~CY', EXPORT_REPORT_DATA_CURRENT_YEAR.toString())
  .replace('~PY', EXPORT_REPORT_DATA_PROJECTED_YEAR.toString());

const getReportFormulaValue = (formula: string, getDemographicValue: (demographicId: string) => null | DemographicValue): number | null => {
  const lookup: Record<string, number> = {};
  const formulaDemographics = getFormulaDemographicIds(formula);
  for (const id of formulaDemographics) {
    try {
      const value = Number(getDemographicValue(id));
      if (value === null || isNaN(value)) {
        return null;
      }
      lookup[id] = value;
    }
    catch (e) {
      return null;
    }
  }

  let result = evaluateFormulaLeftToRight(formula.replace(/%$/, ''), lookup, 6);
  if (result === null) {
    return null;
  }
  else {
    if (formula.endsWith('%')) {
      result = result * 100;
    }

    return result;
  }
};

export const getFormulaTableCellValue = ({
  columnId, formula, templateRow, getDemographicData, formulaRowsById,
}: Readonly<{
  columnId: string;
  formula: string;
  templateRow: ReportTemplateDataRow;
  getDemographicData: GetDemographicData;
  formulaRowsById: ReadonlyMap<string, ReportTemplateDataRow>;
}>): ExportReportTableValue => {
  const formulaDemographicIds = getFormulaDemographicIds(formula);

  const getValue = (demographicId: string) =>
    getTemplateDemographicValue({ columnId, demographicId, formulaRowsById, getDemographicData });

  if (formulaDemographicIds.some(demographicId => !getValue(demographicId))) {
    return REPORT_VALUE_NOT_AVAILABLE;
  }

  const value = getReportFormulaValue(formula, getValue);
  const unit = templateRow.unit ?? '';

  const formatType = formula.endsWith('%')
    ? ReportItemFormatType.PERCENT
    : templateRow.format_type
      ? getTemplateRowFormatType(templateRow.format_type)
      : DEFAULT_REPORT_ITEM_FORMAT_TYPE;

  return {
    value: formatReportValue({ value, formatType, unit }),
    isError: isNullsy(value),
  };
};

export const getDemographicTableCellValue = ({
  columnId, templateRow, getDemographicData, formulaRowsById,
}: Readonly<{
  columnId: string;
  templateRow: ReportTemplateDataRow;
  getDemographicData: GetDemographicData;
  formulaRowsById: ReadonlyMap<string, ReportTemplateDataRow>;
}>): ExportReportTableValue => {
  const demographicValue = getTemplateDemographicValue({ columnId, demographicId: templateRow.id, formulaRowsById, getDemographicData });
  const demographicData = getDemographicData(columnId, templateRow.id);

  if (!demographicData) {
    return REPORT_VALUE_NOT_AVAILABLE;
  }

  const unit = templateRow.unit ?? '';

  const formatType = templateRow.format_type ? getTemplateRowFormatType(templateRow.format_type) : DEFAULT_REPORT_ITEM_FORMAT_TYPE;

  const formattedValue = formatReportValue({ value: demographicValue, formatType, unit });

  return {
    value: formattedValue,
  };
};

const getFetchedDemographicValue = ({ columnId, demographicId, getDemographicData }: Readonly<{
  columnId: string;
  demographicId: string;
  getDemographicData: GetDemographicData;
}>): number | string | null => {
  const demographicData = getDemographicData(columnId, demographicId);
  if (!demographicData || demographicData.value === null) {
    return null;
  }

  return demographicData.value;
};

const getReferencedFormulaRowValue = ({ columnId, formulaRow, getDemographicData }: Readonly<{
  columnId: string;
  formulaRow: ReportTemplateDataRow | undefined;
  getDemographicData: GetDemographicData;
}>): DemographicValue | null => {
  if (!formulaRow?.formula || isReportFormulaIsTooComplex(formulaRow.formula)) {
    return null;
  }

  const value = getReportFormulaValue(formulaRow.formula, demographicId => getFetchedDemographicValue({
    columnId,
    demographicId,
    getDemographicData,
  }));

  return isNullsy(value) ? null : value;
};

const getTemplateDemographicValue = ({ columnId, demographicId, formulaRowsById, getDemographicData }: Readonly<{
  columnId: string;
  demographicId: string;
  formulaRowsById: ReadonlyMap<string, ReportTemplateDataRow>;
  getDemographicData: GetDemographicData;
}>): DemographicValue | null => {
  return getFetchedDemographicValue({ columnId, demographicId, getDemographicData })
    ?? getReferencedFormulaRowValue({ columnId, formulaRow: formulaRowsById?.get(normalizeReportDemographicId(demographicId)), getDemographicData });
};
